/**
 * Phosphor Icons library
 *
 * Mirrors Font Awesome's library + dom.watch() pattern.
 * Call library.add({ bold: { 'icon-name': svgString } }) to register icons,
 * then call watch() to replace <i class="ph-bold ph-icon-name"> with inline <svg>.
 *
 * Sizing: copies font-size from the <i> inline style to the <svg>; the
 * .svg-inline--ph CSS class provides height: 1em — same mechanism as FA.
 *
 * Color: copies color from the <i> inline style to the <svg>; Phosphor SVGs
 * use fill="currentColor" so the CSS color property drives the fill.
 */

export class PhosphorLibrary {
  constructor() {
    this._icons = {};
  }

  add(icons) {
    for (const [weight, set] of Object.entries(icons)) {
      this._icons[weight] = { ...(this._icons[weight] ?? {}), ...set };
    }
  }

  get(weight, name) {
    return this._icons[weight]?.[name] ?? null;
  }

  // doc defaults to the global document; pass an iframe's contentDocument to
  // initialize icons inside a same-origin frame (e.g. WP block editor canvas).
  // Safe to call multiple times — re-entrancy on the same document is a no-op.
  watch(doc = document) {
    if (doc._phWatching) return;
    doc._phWatching = true;
    const init = () => {
      this._replace(doc.querySelectorAll('i[class*="ph"]'), doc);
      if (!doc.body) return;
      const observer = new MutationObserver(mutations => {
        for (const m of mutations) {
          m.addedNodes.forEach(node => {
            if (node.nodeType !== 1) return;
            if (node.matches('i[class*="ph"]')) this._replace([node], doc);
            node.querySelectorAll('i[class*="ph"]').forEach(el => this._replace([el], doc));
          });
        }
      });
      observer.observe(doc.body, { childList: true, subtree: true });
    };
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  _replace(elements, doc = document) {
    const weightMap = {
      ph: 'regular',
      'ph-thin': 'thin',
      'ph-light': 'light',
      'ph-bold': 'bold',
      'ph-fill': 'fill',
      'ph-duotone': 'duotone',
    };

    elements.forEach(el => {
      const classes = [...el.classList];
      const weightClass = classes.find(c => Object.prototype.hasOwnProperty.call(weightMap, c));
      const iconClass = classes.find(c => c.startsWith('ph-') && c !== weightClass);
      if (!weightClass || !iconClass) return;

      const svgStr = this.get(weightMap[weightClass], iconClass.replace(/^ph-/, ''));
      if (!svgStr) return;

      const tmp = doc.createElement('div');
      tmp.innerHTML = svgStr;
      const svg = tmp.querySelector('svg');
      if (!svg) return;

      svg.classList.add('svg-inline--ph', ...classes);
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');

      // Mirror FA: copy font-size and color from the <i> inline style.
      // getPropertyValue/setProperty handle CSS custom property values (var(--primary))
      // correctly, unlike direct CSSOM accessors which may drop them.
      const fontSize = el.style.getPropertyValue('font-size');
      const color = el.style.getPropertyValue('color');
      if (fontSize) svg.style.setProperty('font-size', fontSize);
      if (color) svg.style.setProperty('color', color);

      el.replaceWith(svg);
    });
  }
}

// Each theme tier (framework, parent, child) compiles this file into its own,
// independent webpack build — ES module singletons only hold *within* one
// compilation, so a plain `new PhosphorLibrary()` here would give every tier
// its own disconnected instance. Only whichever tier's code actually calls
// `.watch()` (currently the framework's 01-atoms/svg pattern) would ever
// replace icons — registrations from any other tier would silently never
// render, with no error. Backing the singleton with `window` makes every
// tier's copy of this module resolve to the exact same runtime instance
// instead, regardless of which build produced it.
const globalScope = typeof window !== 'undefined' ? window : globalThis;
export const library = globalScope.__timberlandPhosphorLibrary ?? (globalScope.__timberlandPhosphorLibrary = new PhosphorLibrary());
export const watch = () => library.watch();
